import React, { useState } from 'react';

const SlackApproval = () => {
  const [action, setAction] = useState('');
  const [reason, setReason] = useState('');
  const [status, setStatus] = useState<'idle' | 'posted' | 'approved' | 'rejected'>('idle');
  const [messageTs, setMessageTs] = useState('');
  const [response, setResponse] = useState('');

  const askForApproval = async () => {
    try {
      // Claude posts to Slack asking for approval
      const slackRes = await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post-approval', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: action,
          reason: reason
        })
      });

      const data = await slackRes.json();
      setMessageTs(data.ts);
      setStatus('posted');
      setResponse('Posted to Slack! Waiting for your approval...');

      // Start polling for response
      pollForApproval(data.ts);
    } catch (error) {
      setResponse(`Error: ${error}`);
    }
  };

  const pollForApproval = async (ts: string) => {
    // In real implementation, this would check for button clicks or thread replies
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`https://futr-bet-api.b-9f2.workers.dev/slack/check-approval?ts=${ts}`);
        const data = await res.json();

        if (data.decision) {
          clearInterval(interval);

          if (data.decision === 'approved') {
            setStatus('approved');
            setResponse('✅ Approved! Proceeding with action...');

            // Delete the original message
            await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/delete', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ timestamp: ts })
            });

            // Post final confirmation
            await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                text: `✅ Approved: ${action}\n${data.approvedBy ? `Approved by: ${data.approvedBy}` : ''}`
              })
            });
          } else {
            setStatus('rejected');
            setResponse('❌ Rejected. Action cancelled.');

            // Delete and post rejection
            await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/delete', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ timestamp: ts })
            });

            await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                text: `❌ Rejected: ${action}\nReason: ${data.reason || 'No reason provided'}`
              })
            });
          }
        }
      } catch (error) {
        console.error('Poll error:', error);
      }
    }, 3000); // Check every 3 seconds
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-green-400 p-6">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">🤖 Claude Asks for Approval</h1>
        <p className="text-gray-400 mb-8">
          Claude will post to Slack asking for human approval before taking actions
        </p>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">What action does Claude want to take?</label>
            <input
              type="text"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="Deploy to production"
              className="w-full bg-black text-green-400 p-3 rounded border border-gray-600 focus:border-green-400 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Why does Claude want to do this?</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="All tests are passing and the feature is ready for production"
              className="w-full h-24 bg-black text-green-400 p-3 rounded border border-gray-600 focus:border-green-400 focus:outline-none"
            />
          </div>

          <button
            onClick={askForApproval}
            disabled={!action || !reason || status !== 'idle'}
            className="w-full px-6 py-3 bg-green-600 text-black rounded-lg hover:bg-green-500 transition-colors font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Ask for Approval in Slack
          </button>
        </div>

        {/* What gets posted to Slack */}
        <div className="mt-8 bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-xl font-bold mb-4">What Claude Posts to Slack</h2>
          <div className="bg-black p-4 rounded font-mono text-sm">
            <p className="text-yellow-400">🤖 Claude needs approval:</p>
            <p className="text-white mt-2">Action: {action || '[Your action]'}</p>
            <p className="text-white">Reason: {reason || '[Your reason]'}</p>
            <div className="mt-4 flex gap-2">
              <span className="bg-green-600 text-black px-3 py-1 rounded">✅ Approve</span>
              <span className="bg-red-600 text-white px-3 py-1 rounded">❌ Reject</span>
            </div>
          </div>
        </div>

        {/* Status */}
        {status !== 'idle' && (
          <div className="mt-8 bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h2 className="text-xl font-bold mb-4">Status</h2>
            <div className="space-y-2">
              <div className={`flex items-center gap-3 ${status === 'posted' || status === 'approved' || status === 'rejected' ? '' : 'opacity-50'}`}>
                <div className={`w-3 h-3 rounded-full ${status !== 'idle' ? 'bg-green-500' : 'bg-gray-600'}`} />
                <span>1. Posted to Slack</span>
              </div>
              <div className={`flex items-center gap-3 ${status === 'approved' || status === 'rejected' ? '' : 'opacity-50'}`}>
                <div className={`w-3 h-3 rounded-full ${status === 'approved' || status === 'rejected' ? 'bg-green-500' : 'bg-gray-600'}`} />
                <span>2. Human responded</span>
              </div>
              <div className={`flex items-center gap-3 ${status === 'approved' || status === 'rejected' ? '' : 'opacity-50'}`}>
                <div className={`w-3 h-3 rounded-full ${status === 'approved' || status === 'rejected' ? 'bg-green-500' : 'bg-gray-600'}`} />
                <span>3. Original message deleted</span>
              </div>
              <div className={`flex items-center gap-3 ${status === 'approved' || status === 'rejected' ? '' : 'opacity-50'}`}>
                <div className={`w-3 h-3 rounded-full ${status === 'approved' || status === 'rejected' ? 'bg-green-500' : 'bg-gray-600'}`} />
                <span>4. Final result posted</span>
              </div>
            </div>
            {response && (
              <div className="mt-4 p-3 bg-black rounded border border-green-400">
                <p className="text-sm">{response}</p>
              </div>
            )}
          </div>
        )}

        {/* How it works */}
        <div className="mt-8 bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-xl font-bold mb-4">The "Human Assist" Pattern</h2>
          <p className="text-gray-400 mb-4">
            This gives Claude the ability to ask for human approval before taking actions:
          </p>
          <ol className="space-y-2 text-sm text-gray-400">
            <li>1. Claude posts to Slack with action buttons</li>
            <li>2. Human clicks Approve or Reject in Slack</li>
            <li>3. Claude receives the decision</li>
            <li>4. Claude deletes the question (keeps channel clean)</li>
            <li>5. Claude posts only the final decision</li>
            <li>6. Claude proceeds or cancels based on approval</li>
          </ol>
          <p className="mt-4 text-xs text-gray-500">
            Result: Claude can ask for permission, you stay in control!
          </p>
        </div>
      </div>
    </div>
  );
};

export default SlackApproval;